import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { HardHat, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import api from '../api/client';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(initialEmail);
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  const validation = useMemo(() => {
    const checks = {
      minLength: password.length >= 8,
      lowerCase: /[a-z]/.test(password),
      upperCase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[^A-Za-z\d]/.test(password),
      match: password.length > 0 && password === passwordConfirmation,
    };
    const isValid =
      checks.minLength &&
      checks.lowerCase &&
      checks.upperCase &&
      checks.number &&
      checks.special &&
      checks.match;
    return { checks, isValid };
  }, [password, passwordConfirmation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading || !validation.isValid) return;

    setError(null);
    setIsLoading(true);

    try {
      await api.post('/api/v1/auth/reset-password', {
        email,
        token: token.toUpperCase(),
        password,
        password_confirmation: passwordConfirmation,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { message?: string; error?: string; errors?: Record<string, string[]> } };
        message?: string;
      };
      let msg = 'İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.';
      if (axiosErr.response?.data?.message) {
        msg = axiosErr.response.data.message;
      } else if (axiosErr.response?.data?.error) {
        msg = axiosErr.response.data.error;
      } else if (axiosErr.response?.data?.errors) {
        const errors = axiosErr.response.data.errors;
        msg = Object.values(errors).flat().join(' ');
      } else if (axiosErr.message) {
        msg = axiosErr.message;
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const CheckItem = ({ ok, text }: { ok: boolean; text: string }) => (
    <li className={`flex items-center text-xs ${ok ? 'text-green-600' : 'text-slate-500'}`}>
      <span
        className={`mr-2 flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
          ok ? 'bg-green-100' : 'bg-slate-100'
        }`}
      >
        {ok ? '✓' : '•'}
      </span>
      {text}
    </li>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-600 text-white">
            <HardHat className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Şifre Sıfırlama</h1>
          <p className="mt-1 text-sm text-slate-500">
            E-postanıza gelen doğrulama kodunu ve yeni şifrenizi girin
          </p>
        </div>

        {error && (
          <div
            data-testid="reset-error-message"
            className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            data-testid="reset-success-message"
            className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm font-medium text-green-700"
          >
            Şifreniz başarıyla sıfırlandı. Giriş sayfasına yönlendiriliyorsunuz...
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              E-posta
            </label>
            <input
              id="email"
              data-testid="reset-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="ornek@rizmir.com"
            />
          </div>

          <div>
            <label
              htmlFor="token"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Doğrulama Kodu
            </label>
            <input
              id="token"
              data-testid="reset-token"
              type="text"
              required
              maxLength={6}
              value={token}
              onChange={(e) => setToken(e.target.value.toUpperCase())}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-center text-sm font-semibold uppercase tracking-[0.3em] text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder="------"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Yeni Şifre
            </label>
            <div className="relative">
              <input
                id="password"
                data-testid="reset-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="••••••••"
              />
              <button
                type="button"
                data-testid="reset-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="passwordConfirmation"
              className="mb-1 block text-sm font-medium text-slate-700"
            >
              Yeni Şifre (Tekrar)
            </label>
            <div className="relative">
              <input
                id="passwordConfirmation"
                data-testid="reset-password-confirmation"
                type={showPasswordConfirmation ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="••••••••"
              />
              <button
                type="button"
                data-testid="reset-password-confirmation-toggle"
                onClick={() => setShowPasswordConfirmation((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showPasswordConfirmation ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <ul className="space-y-1 rounded-lg bg-slate-50 p-3">
            <CheckItem ok={validation.checks.minLength} text="En az 8 karakter" />
            <CheckItem ok={validation.checks.upperCase} text="En az 1 büyük harf" />
            <CheckItem ok={validation.checks.lowerCase} text="En az 1 küçük harf" />
            <CheckItem ok={validation.checks.number} text="En az 1 rakam" />
            <CheckItem ok={validation.checks.special} text="En az 1 özel karakter" />
            <CheckItem ok={validation.checks.match} text="Şifreler eşleşiyor" />
          </ul>

          <button
            type="submit"
            data-testid="reset-submit-button"
            disabled={isLoading || success || !validation.isValid}
            className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                İşleniyor...
              </>
            ) : (
              'Şifremi Sıfırla'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            data-testid="reset-back-to-login"
            className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Giriş sayfasına dön
          </Link>
        </div>
      </div>
    </div>
  );
}
